import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Send, Plus, Trash2, BarChart3 } from "lucide-react";

const STATUS_LABEL = {
  draft: "Bozza",
  scheduled: "Programmata",
  sending: "In invio",
  sent: "Inviata",
  failed: "Fallita",
};
const STATUS_VARIANT = {
  draft: "secondary",
  scheduled: "outline",
  sending: "outline",
  sent: "default",
  failed: "destructive",
};

export default function CampaignsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  const load = () => api.get("/campaigns").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);

  const del = async (id) => {
    if (!window.confirm("Eliminare questa campagna?")) return;
    await api.delete(`/campaigns/${id}`);
    toast.success("Campagna eliminata");
    load();
  };

  return (
    <>
      <PageHeader
        title="Campagne"
        subtitle="Le tue newsletter, bozze e programmazioni."
        testid="campaigns-header"
        actions={
          <Button onClick={() => navigate("/campagne/nuova")} data-testid="new-campaign-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuova campagna
          </Button>
        }
      />
      <PageBody>
        {items.length === 0 && (
          <div className="border border-dashed border-border bg-white p-16 text-center">
            <Send className="w-8 h-8 mx-auto text-muted-foreground mb-4" strokeWidth={1.25} />
            <h2 className="font-heading text-xl mb-2">Nessuna campagna ancora</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Crea la tua prima newsletter e inviala dal tuo Microsoft 365.
            </p>
            <Button onClick={() => navigate("/campagne/nuova")} data-testid="empty-new-campaign">
              Crea la prima campagna
            </Button>
          </div>
        )}

        <div className="space-y-3">
          {items.map((c) => {
            const stats = c.stats || {};
            const openRate = stats.sent ? Math.round((stats.opens / stats.sent) * 100) : 0;
            return (
              <div
                key={c.id}
                className="border border-border bg-white p-5 flex items-center gap-6 hover:bg-muted/40 cursor-pointer transition-colors"
                onClick={() => navigate(c.status === "sent" ? `/campagne/${c.id}/report` : `/campagne/${c.id}`)}
                data-testid={`campaign-row-${c.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-heading text-lg truncate">{c.name}</span>
                    <Badge variant={STATUS_VARIANT[c.status]}>
                      {STATUS_LABEL[c.status] || c.status}
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground truncate">
                    {c.subject}
                  </div>
                  {c.scheduled_at && c.status === "scheduled" && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Programmata per: {new Date(c.scheduled_at).toLocaleString("it-IT", { timeZone: "Europe/Rome" })}
                    </div>
                  )}
                </div>
                <div className="hidden md:flex items-center gap-6 text-center">
                  <div>
                    <div className="font-heading text-lg">{stats.sent || 0}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Inviate</div>
                  </div>
                  <div>
                    <div className="font-heading text-lg">{openRate}%</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Aperture</div>
                  </div>
                  <div>
                    <div className="font-heading text-lg">{stats.clicks || 0}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Click</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {c.status === "sent" && (
                    <BarChart3 className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      del(c.id);
                    }}
                    data-testid={`delete-campaign-${c.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </PageBody>
    </>
  );
}
